import { useEffect, useRef } from 'react';
import * as Api from '../api';

// Signed URLs from /uploads/sign are valid 1h. Re-signing a key set that we
// already signed recently is pure waste (see the revision note below).
const SIGN_RETRY_MS = 50 * 60 * 1000;

/**
 * Rewrite <img> srcs inside a DOM subtree with fresh signed URLs.
 *
 * Why this exists: bucket is Private (CLAUDE.md §6). Saved HTML carries
 * `<img data-b2-key="...">` plus a possibly-expired `src`. On mount the
 * renderer/editor sweeps the DOM, collects unique keys, batch-asks the api
 * to sign them, and swaps `src` in place.
 *
 * Also handles legacy <img> nodes that only have a B2-pseudo-public src
 * (pre-`data-b2-key` content): we parse the key out of the URL path.
 *
 * Inputs:
 *  - `rootRef`: ref to the container whose <img> nodes we should heal.
 *  - `revision`: bump this whenever the container's innerHTML changes so
 *    the effect re-runs (e.g. tie to the prop that owns the HTML string).
 *
 * The effect may re-run on *every keystroke* when the caller ties `revision`
 * to live editor HTML, so we remember the last signed key set and skip the
 * API call when the images haven't changed (URLs are still fresh for ~1h).
 */
export function useSignedImageRehydrate(
  rootRef: React.RefObject<HTMLElement | null>,
  revision: unknown,
) {
  const lastSignRef = useRef<{ keys: Set<string>; at: number } | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let cancelled = false;

    const queue = collectKeys(root);
    if (queue.size === 0) return;

    const previous = lastSignRef.current;
    if (
      previous &&
      setsEqual(previous.keys, queue) &&
      Date.now() - previous.at < SIGN_RETRY_MS
    ) {
      return;
    }

    (async () => {
      try {
        const response = await Api.SignImages({ keys: Array.from(queue) });
        if (cancelled) return;
        const urls = response.data.data.urls;
        applySignedUrls(root, urls);
        lastSignRef.current = { keys: queue, at: Date.now() };
      } catch {
        // Surface failures via the broken-image fallback (alt text). A toast
        // here would fire on every editor mount for users whose token has
        // lapsed — not worth the noise.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rootRef, revision]);
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

/**
 * Collect every signable key inside the subtree. Prefers `data-b2-key`;
 * falls back to parsing the path out of legacy B2 URLs.
 */
function collectKeys(root: HTMLElement): Set<string> {
  const keys = new Set<string>();
  const images = root.querySelectorAll<HTMLImageElement>('img');
  images.forEach((img) => {
    const key = resolveKey(img);
    if (key) keys.add(key);
  });
  return keys;
}

function applySignedUrls(root: HTMLElement, urls: Record<string, string>) {
  const images = root.querySelectorAll<HTMLImageElement>('img');
  images.forEach((img) => {
    const key = resolveKey(img);
    if (!key) return;
    const next = urls[key];
    if (!next) return;
    if (img.getAttribute('src') !== next) img.setAttribute('src', next);
    if (!img.getAttribute('data-b2-key')) img.setAttribute('data-b2-key', key);
  });
}

function resolveKey(img: HTMLImageElement): string | null {
  const attrKey = img.getAttribute('data-b2-key');
  if (attrKey && isSignable(attrKey)) return attrKey;

  const src = img.getAttribute('src');
  if (!src) return null;
  const fromSrc = extractKeyFromUrl(src);
  if (fromSrc && isSignable(fromSrc)) return fromSrc;

  return null;
}

/** The path-only fallback is gated by `isSignable()`, so arbitrary keys can't be smuggled through. */
function extractKeyFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/^\/+/, '');
    const fileMatch = path.match(/^file\/[^/]+\/(.+)$/);
    if (fileMatch) return decodeURIComponent(fileMatch[1]);
    if (/backblazeb2\.com$/.test(parsed.hostname)) {
      const s3Match = path.match(/^[^/]+\/(.+)$/);
      if (s3Match) return decodeURIComponent(s3Match[1]);
    }
    return decodeURIComponent(path);
  } catch {
    return null;
  }
}

/** Mirrors SIGNABLE_IMAGE_KEY in apps/api/src/common/rich-images.ts (a parity test keeps them equal). */
export const SIGNABLE_IMAGE_KEY =
  /^(editor|question-bank|questions|tests|section-library|question-media\/import)\/[A-Za-z0-9_./-]+$/;

function isSignable(key: string): boolean {
  return SIGNABLE_IMAGE_KEY.test(key) && !key.includes('..');
}

export default useSignedImageRehydrate;
